import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { RefreshCw } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";

const registrationTypes = [
  "Business Registration Number",
  "MyKad",
  "MyTentera",
  "Passport",
  "MyPR",
  "MyKAS",
];

const states = [
  { code: "01", name: "Johor" },
  { code: "02", name: "Kedah" },
  { code: "03", name: "Kelantan" },
  { code: "04", name: "Melaka" },
  { code: "05", name: "Negeri Sembilan" },
  { code: "06", name: "Pahang" },
  { code: "07", name: "Pulau Pinang" },
  { code: "08", name: "Perak" },
  { code: "09", name: "Perlis" },
  { code: "10", name: "Selangor" },
  { code: "11", name: "Terengganu" },
  { code: "12", name: "Sabah" },
  { code: "13", name: "Sarawak" },
  { code: "14", name: "Wilayah Persekutuan Kuala Lumpur" },
  { code: "15", name: "Wilayah Persekutuan Labuan" },
  { code: "16", name: "Wilayah Persekutuan Putrajaya" },
  { code: "17", name: "Not Applicable" },
];

const initialValues = {
  name: "",
  tin: "",
  registrationType: "",
  registrationNumber: "",
  sstRegistrationNumber: "",
  tourismTaxRegistrationNumber: "",
  email: "",
  msicCode: "00000",
  businessActivityDescription: "",
  contactNumber: "",
  address: {
    country: "MYS",
  },
};

const formSchema = z.object({
  name: z.string().min(1, "Company name is required"),
  tin: z.string().min(1, "TIN is required"),
  registrationType: z.string({
    required_error: "Please select a registration type",
  }),
  registrationNumber: z.string().refine((val) => {
    if (!val) return false;
    const type = (
      document.querySelector('[name="registrationType"]') as HTMLSelectElement
    )?.value;
    switch (type) {
      case "MyKad":
      case "MyTentera":
      case "Passport":
      case "MyPR":
      case "MyKAS":
        return val.length <= 12;
      case "Business Registration Number":
        return val.length <= 20;
      default:
        return true;
    }
  }, "Invalid registration number length for selected type"),
  sstRegistrationNumber: z
    .string()
    .regex(
      /^(NA|[A-Za-z0-9-]+(;[A-Za-z0-9-]+)?)?$/,
      "Invalid SST registration number format"
    )
    .optional()
    .nullable(),
  tourismTaxRegistrationNumber: z.string().optional().nullable(),
  email: z.string().email("Invalid email address"),
  msicCode: z.string().regex(/^\d{5}$/, "MSIC must be a 5-digit numeric code"),
  businessActivityDescription: z
    .string()
    .min(1, "Business activity description is required"),
  contactNumber: z
    .string()
    .regex(/^\+?[0-9-]+$/, "Invalid phone number format"),
  address: z.object({
    addressLine0: z.string().min(1, "Address line 1 is required"),
    addressLine1: z.string().optional(),
    addressLine2: z.string().optional().nullable(),
    postalZone: z.string().regex(/^\d+$/, "Postal code must be numeric"),
    cityName: z.string().min(1, "City is required"),
    state: z.string({
      required_error: "Please select a state",
    }),
    country: z
      .string()
      .regex(/^[A-Z]{3}$/, "Must be a 3-letter ISO country code"),
  }),
});

export const SupplierForm = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    alert(data)
  };

  const resetForm = () => {
    for (const [key, value] of Object.entries(initialValues)) {
      if (typeof value === "object") {
        for (const [nestedKey, nestedValue] of Object.entries(value)) {
          form.setValue(
            `${key}.${nestedKey}` as keyof typeof initialValues,
            nestedValue
          );
        }
      } else {
        form.setValue(key as keyof typeof initialValues, value);
      }
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="border-b">
        <CardTitle className="text-2xl font-bold">Supplier Details</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {/* Left Column */}
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name *</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="registrationType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Registration Type *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select registration type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {registrationTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="registrationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Registration Number *</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sstRegistrationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SST Registration Number</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormDescription>
                        Enter NA if not registered. Max 2 SST separated by
                        semicolon
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Number *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="businessActivityDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Activity Description *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="msicCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>MSIC Code *</FormLabel>
                      <FormControl>
                        <Input {...field} maxLength={5} />
                      </FormControl>
                      <FormDescription>5-digit numeric code</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold mb-4">Address Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <FormField
                  control={form.control}
                  name="address.addressLine0"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 1 *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.addressLine1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 2</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.addressLine2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 3</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.postalZone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal Code *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.cityName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {states.map((state) => (
                            <SelectItem key={state.code} value={state.code}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country Code *</FormLabel>
                      <FormControl>
                        <Input {...field} maxLength={3} />
                      </FormControl>
                      <FormDescription>
                        3-letter ISO country code (e.g., MYS)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex gap-4 justify-end pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </Button>
              <Button type="submit">Submit</Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
